import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, CheckCircle, XCircle, Share2, RotateCcw, Trophy, Award, Medal, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const MineralQuizPage = () => {
    const { language } = useLanguage();
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [score, setScore] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [quizComplete, setQuizComplete] = useState(false);

    const t = (key) => translations[language]?.[key] || translations['no'][key] || key;

    const questions = language === 'no' ? questionsNO : questionsEN;

    const handleAnswerSelect = (answerIndex) => {
        if (showResult) return;
        setSelectedAnswer(answerIndex);
    };

    const handleConfirmAnswer = () => {
        if (selectedAnswer === null) return;
        
        const isCorrect = selectedAnswer === questions[currentQuestion].correct;
        if (isCorrect) {
            setScore(score + 1);
        }
        
        setAnswers([...answers, { 
            question: currentQuestion, 
            selected: selectedAnswer, 
            correct: questions[currentQuestion].correct,
            isCorrect 
        }]);
        setShowResult(true);
    };

    const handleNextQuestion = () => {
        if (currentQuestion < questions.length - 1) {
            setCurrentQuestion(currentQuestion + 1);
            setSelectedAnswer(null);
            setShowResult(false);
        } else {
            setQuizComplete(true);
        }
    };

    const handleRestartQuiz = () => {
        setCurrentQuestion(0);
        setSelectedAnswer(null);
        setShowResult(false);
        setScore(0);
        setAnswers([]);
        setQuizComplete(false);
    };

    const getResultLevel = () => {
        const percentage = (score / questions.length) * 100;
        if (percentage >= 90) return { level: t('expert'), icon: Trophy, color: 'text-yellow-500' };
        if (percentage >= 70) return { level: t('enthusiast'), icon: Award, color: 'text-blue-500' };
        if (percentage >= 50) return { level: t('apprentice'), icon: Medal, color: 'text-orange-500' };
        return { level: t('beginner'), icon: Brain, color: 'text-stone-500' };
    };

    const shareResult = (platform) => {
        const result = getResultLevel();
        const percentage = Math.round((score / questions.length) * 100);
        const shareText = language === 'no' 
            ? `Jeg fikk ${score} av ${questions.length} riktige (${percentage}%) på Raw Minerals sin mineralvann-quiz og ble "${result.level}"! Test din kunnskap:`
            : `I got ${score} out of ${questions.length} correct (${percentage}%) on Raw Minerals' mineral water quiz and became "${result.level}"! Test your knowledge:`;
        
        const shareUrl = 'https://rawminerals.no/mineral-quiz';
        
        const urls = {
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
            twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
            copy: null
        };

        if (platform === 'copy') {
            navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
            alert(language === 'no' ? 'Kopiert til utklippstavle!' : 'Copied to clipboard!');
        } else {
            window.open(urls[platform], '_blank', 'width=600,height=400');
        }
    };

    return (
        <div className="page-transition min-h-screen bg-stone-50" data-testid="mineral-quiz-page">
            <SEO 
                title={t('pageTitle')}
                description={t('pageDescription')}
                canonical="/mineral-quiz"
            />

            {/* Hero Section */}
            <section className="py-16 md:py-24 bg-fjord text-white">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center max-w-3xl mx-auto"
                    >
                        <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-4">
                            {t('quizSubtitle')}
                        </p>
                        <h1 className="font-display text-4xl md:text-5xl font-light mb-4">
                            {t('quizTitle')}
                        </h1>
                        <p className="text-white/70 text-lg">
                            {t('quizDescription')}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Quiz Content */}
            <section className="py-16 md:py-24">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <AnimatePresence mode="wait">
                        {!quizComplete ? (
                            <motion.div
                                key={`question-${currentQuestion}`}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                            >
                                {/* Progress Bar */}
                                <div className="mb-8">
                                    <div className="flex justify-between text-sm text-stone-500 mb-2">
                                        <span>{t('question')} {currentQuestion + 1} {t('of')} {questions.length}</span>
                                        <span>{score} {t('correct')}</span>
                                    </div>
                                    <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                                        <motion.div 
                                            className="h-full bg-fjord"
                                            initial={{ width: 0 }}
                                            animate={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
                                            transition={{ duration: 0.3 }}
                                        />
                                    </div>
                                </div>

                                {/* Question Card */}
                                <div className="bg-white border border-stone-200 p-8 md:p-12 mb-6">
                                    <h2 className="font-display text-xl md:text-2xl mb-8">
                                        {questions[currentQuestion].question}
                                    </h2>

                                    <div className="space-y-3">
                                        {questions[currentQuestion].options.map((option, index) => {
                                            const isSelected = selectedAnswer === index;
                                            const isCorrect = index === questions[currentQuestion].correct;
                                            
                                            let buttonStyle = 'border-stone-200 hover:border-fjord/50';
                                            if (showResult) {
                                                if (isCorrect) {
                                                    buttonStyle = 'border-green-500 bg-green-50';
                                                } else if (isSelected && !isCorrect) {
                                                    buttonStyle = 'border-red-500 bg-red-50';
                                                }
                                            } else if (isSelected) {
                                                buttonStyle = 'border-fjord bg-fjord/5';
                                            }

                                            return (
                                                <button
                                                    key={index}
                                                    onClick={() => handleAnswerSelect(index)}
                                                    disabled={showResult}
                                                    className={`w-full text-left p-4 border-2 rounded transition-all ${buttonStyle} ${!showResult && 'cursor-pointer'}`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <span className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-medium ${isSelected ? 'border-fjord bg-fjord text-white' : 'border-stone-300'}`}>
                                                            {String.fromCharCode(65 + index)}
                                                        </span>
                                                        <span className="flex-1">{option}</span>
                                                        {showResult && isCorrect && (
                                                            <CheckCircle className="w-5 h-5 text-green-500" />
                                                        )}
                                                        {showResult && isSelected && !isCorrect && (
                                                            <XCircle className="w-5 h-5 text-red-500" />
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Explanation */}
                                    <AnimatePresence>
                                        {showResult && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                className="mt-6 p-4 bg-stone-50 border border-stone-200 rounded"
                                            >
                                                <p className="text-sm text-stone-600">
                                                    <span className="font-medium">{t('explanation')}:</span> {questions[currentQuestion].explanation}
                                                </p>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex justify-end gap-4">
                                    {!showResult ? (
                                        <button
                                            onClick={handleConfirmAnswer}
                                            disabled={selectedAnswer === null}
                                            className={`px-8 py-3 transition-colors ${selectedAnswer !== null ? 'bg-fjord text-white hover:bg-fjord/90' : 'bg-stone-200 text-stone-400 cursor-not-allowed'}`}
                                        >
                                            {t('confirmAnswer')}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleNextQuestion}
                                            className="px-8 py-3 bg-fjord text-white hover:bg-fjord/90 transition-colors flex items-center gap-2"
                                        >
                                            {currentQuestion < questions.length - 1 ? t('nextQuestion') : t('seeResults')}
                                            <ArrowRight className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </motion.div>
                        ) : (
                            /* Results Section */
                            <motion.div
                                key="results"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5 }}
                                className="text-center"
                            >
                                {/* Result Card */}
                                <div className="bg-white border border-stone-200 p-8 md:p-12 mb-8">
                                    {(() => {
                                        const result = getResultLevel();
                                        const ResultIcon = result.icon;
                                        const percentage = Math.round((score / questions.length) * 100);
                                        
                                        return (
                                            <>
                                                <motion.div
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    transition={{ delay: 0.2, type: 'spring' }}
                                                    className={`w-24 h-24 mx-auto mb-6 rounded-full bg-stone-100 flex items-center justify-center ${result.color}`}
                                                >
                                                    <ResultIcon className="w-12 h-12" />
                                                </motion.div>
                                                
                                                <p className="text-sm uppercase tracking-wider text-stone-500 mb-2">
                                                    {t('yourResult')}
                                                </p>
                                                <h2 className="font-display text-3xl md:text-4xl mb-2">
                                                    {result.level}
                                                </h2>
                                                <p className="text-5xl font-light text-fjord mb-4">
                                                    {score}/{questions.length}
                                                </p>
                                                <p className="text-stone-500 mb-8">
                                                    {t('youGot')} {percentage}% {t('correctAnswers')}
                                                </p>

                                                {/* Score Breakdown */}
                                                <div className="flex justify-center gap-2 mb-8">
                                                    {answers.map((answer, index) => (
                                                        <div
                                                            key={index}
                                                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${answer.isCorrect ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}
                                                        >
                                                            {index + 1}
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Share Section */}
                                                <div className="border-t border-stone-200 pt-8">
                                                    <p className="text-sm text-stone-500 mb-4 flex items-center justify-center gap-2">
                                                        <Share2 className="w-4 h-4" />
                                                        {t('shareResult')}
                                                    </p>
                                                    <div className="flex justify-center gap-3">
                                                        <button
                                                            onClick={() => shareResult('facebook')}
                                                            className="w-12 h-12 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:opacity-90 transition-opacity"
                                                            aria-label="Share on Facebook"
                                                        >
                                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                                                        </button>
                                                        <button
                                                            onClick={() => shareResult('twitter')}
                                                            className="w-12 h-12 rounded-full bg-black text-white flex items-center justify-center hover:opacity-90 transition-opacity"
                                                            aria-label="Share on X"
                                                        >
                                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                                                        </button>
                                                        <button
                                                            onClick={() => shareResult('linkedin')}
                                                            className="w-12 h-12 rounded-full bg-[#0A66C2] text-white flex items-center justify-center hover:opacity-90 transition-opacity"
                                                            aria-label="Share on LinkedIn"
                                                        >
                                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                                                        </button>
                                                        <button
                                                            onClick={() => shareResult('copy')}
                                                            className="w-12 h-12 rounded-full bg-stone-200 text-stone-600 flex items-center justify-center hover:bg-stone-300 transition-colors"
                                                            aria-label="Copy link"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>

                                {/* Action Buttons */}
                                <div className="flex flex-col sm:flex-row justify-center gap-4">
                                    <button
                                        onClick={handleRestartQuiz}
                                        className="px-8 py-3 border border-fjord text-fjord hover:bg-fjord hover:text-white transition-colors flex items-center justify-center gap-2"
                                    >
                                        <RotateCcw className="w-4 h-4" />
                                        {t('tryAgain')}
                                    </button>
                                    <Link
                                        to="/mineral-guide"
                                        className="px-8 py-3 bg-fjord text-white hover:bg-fjord/90 transition-colors flex items-center justify-center gap-2"
                                    >
                                        {t('learnMore')}
                                        <ArrowRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </section>
        </div>
    );
};

// Norwegian Questions
const questionsNO = [
    {
        question: "Hva er hovedforskjellen mellom naturlig mineralvann og vanlig springvann?",
        options: [
            "Naturlig mineralvann har alltid tilsatt kullsyre for å gi det en frisk smak",
            "Naturlig mineralvann inneholder mineraler fra geologiske kilder og er ubehandlet",
            "Det er ingen reell forskjell mellom dem, kun ulik markedsføring og emballasje",
            "Springvann har generelt høyere mineralinnhold fordi det renses grundigere"
        ],
        correct: 1,
        explanation: "Naturlig mineralvann kommer fra beskyttede underjordiske kilder og inneholder mineraler kroppen trenger – som magnesium, kalsium og bikarbonat – i en form som er lett for kroppen å ta opp. Springvann er ofte filtrert og renset, noe som fjerner både forurensninger og naturlige mineraler."
    },
    {
        question: "Hvilket mineral kalles ofte 'avslapningsmineralet' og er viktig for søvn og stressreduksjon?",
        options: [
            "Kalsium, som støtter nervesystemet og gir bedre søvnkvalitet over tid",
            "Natrium, som regulerer kroppens væskebalanse og påvirker nervefunksjon",
            "Magnesium, som støtter nervesystemet og fremmer dyp, restituerende søvn",
            "Kalium, som balanserer elektrolytter og bidrar til muskelavslapning"
        ],
        correct: 2,
        explanation: "Magnesium kalles ofte 'avslapningsmineralet' fordi det støtter nervesystemet, fremmer dyp søvn og reduserer stress og angst. Over 300 enzymatiske prosesser i kroppen er avhengige av magnesium."
    },
    {
        question: "Hva betyr TDS (Total Dissolved Solids) når det gjelder mineralvann?",
        options: [
            "Vannets temperatur målt ved selve kilden der det tappes på flaske",
            "Den totale mengden oppløste mineraler og sporstoffer i vannet",
            "Vannets pH-verdi som indikerer om det er surt eller basisk",
            "Hvor mange år vannet har brukt på sin reise gjennom berggrunnen"
        ],
        correct: 1,
        explanation: "TDS måler den totale mengden oppløste mineraler i vannet, uttrykt i mg/L. Lavt TDS (under 500) gir lett, nøytral smak. Middels TDS (500-1500) gir mer karakter. Høyt TDS (over 1500) gir kraftig mineralsmak."
    },
    {
        question: "Hvorfor er mineraler i naturlig mineralvann lettere for kroppen å ta opp enn kosttilskudd?",
        options: [
            "Fordi mineralvannet har en lavere temperatur som gjør absorpsjonen mer effektiv",
            "Fordi mineralene allerede er i ionisk form og kan absorberes direkte",
            "Fordi mineralvann smaker bedre og dermed stimulerer fordøyelsessystemet",
            "Det er faktisk ingen dokumentert forskjell i hvordan kroppen tar opp mineraler"
        ],
        correct: 1,
        explanation: "Mineraler i naturlig mineralvann er allerede i ionisk form, noe som betyr at kroppen kan absorbere dem direkte uten å måtte bryte dem ned først. Kosttilskudd må ofte gjennom fordøyelsen før de kan tas opp."
    },
    {
        question: "Hvilken funksjon har bikarbonat i mineralvann?",
        options: [
            "Det gir vannet en karakteristisk farge som varierer fra kilde til kilde",
            "Det fungerer som en naturlig buffer mot syre og støtter fordøyelsen",
            "Det senker vannets temperatur og holder det kjølig over lengre tid",
            "Det øker vannets energiinnhold og gir en naturlig oppkvikkende effekt"
        ],
        correct: 1,
        explanation: "Bikarbonat fungerer som en naturlig buffer mot melkesyre ved trening og støtter kroppens syre-base-balanse. Det kan også lindre halsbrann og sure oppstøt, og støtter leverens avgiftningsprosesser."
    },
    {
        question: "Hvorfor har Norge mineralfattig drikkevann?",
        options: [
            "På grunn av industriell forurensning som har påvirket grunnvannet over tid",
            "Fordi berggrunnen består av harde bergarter som granitt som avgir lite mineraler",
            "Fordi det regner så mye at mineralene konstant vaskes ut av jordsmonnet",
            "På grunn av intensiv vannbehandling som fjerner de fleste naturlige mineraler"
        ],
        correct: 1,
        explanation: "Norges berggrunn består hovedsakelig av harde, krystallinske bergarter som granitt og gneis, som avgir svært lite mineraler til grunnvannet. Land med sedimentære bergarter eller kalksteinsformasjoner har naturlige forutsetninger for mineralrikt vann."
    },
    {
        question: "Hva er elektrolytter og hvorfor er de viktige?",
        options: [
            "Naturlige bakteriekulturer som bidrar til å rense og balansere vannet",
            "Mineraler med elektrisk ladning som regulerer væskebalanse og muskelfunksjon",
            "Spesielle vitaminer som gir kroppen energi og øker forbrenningen",
            "Proteiner som kroppen bruker til å bygge og reparere muskelvev"
        ],
        correct: 1,
        explanation: "Elektrolytter er mineraler med elektrisk ladning som regulerer væskebalanse, nervesignaler og muskelkontraksjoner. De viktigste er natrium, kalium, magnesium og kalsium. Ubalanse kan føre til kramper og utmattelse."
    },
    {
        question: "I finere gastronomi, hvordan kan mineralvann påvirke matopplevelsen?",
        options: [
            "Det har ingen målbar effekt på hvordan vi opplever smaken av mat",
            "Det kjøler ned maten og gir en mer behagelig spisetemperatur",
            "Mineralinnholdet påvirker munnfølelse og kan komplementere eller fremheve smaker",
            "Det fungerer primært som en erstatning for salt i tilberedningen"
        ],
        correct: 2,
        explanation: "Mineralvann har distinkte smaksprofiler som kan komplementere mat og vin. Vann med høy mineralitet kan fremheve smaken i rike retter, mens lettere vann passer til delikate smaker. Det handler om den totale matopplevelsen."
    },
    {
        question: "Hvilket av disse landene er IKKE kjent for premium mineralvann med høyt mineralinnhold?",
        options: [
            "Slovenia, som har flere verdenskjente kilder med eksepsjonelt mineralinnhold",
            "Norge, som har noen av Europas reneste men også mest mineralfattige vannkilder",
            "Tyskland, som er hjemland for mange av verdens mest anerkjente mineralvann",
            "Italia, som har en lang tradisjon for naturlig mineralvann fra alpine kilder"
        ],
        correct: 1,
        explanation: "Norge har mineralfattig drikkevann på grunn av berggrunnen. Slovenia (ROI), Tyskland (Gerolsteiner) og Italia (Marzia) har alle geologiske forhold som gir naturlig mineralrikt vann."
    },
    {
        question: "Hvorfor bruker premium mineralvannprodusenter ofte glassflasker?",
        options: [
            "Hovedsakelig for å gi produktet et mer eksklusivt og premium utseende",
            "Glass kan resirkuleres uendelig, avgir ingen stoffer og beskytter vannets kvalitet bedre",
            "Fordi plastflasker faktisk er dyrere å produsere i store mengder",
            "Det er et lovpålagt krav i EU for alt vann som selges som premium"
        ],
        correct: 1,
        explanation: "Glass kan resirkuleres uendelig mange ganger uten tap av kvalitet, avgir ingen mikroplast eller kjemikalier til vannet, og beskytter vannets kvalitet bedre enn plast som kan påvirkes av lys og temperatur."
    },
    {
        question: "Hvor lang tid kan det ta før regnvann blir til naturlig mineralvann?",
        options: [
            "Vanligvis bare noen dager, avhengig av jordforholdene i området",
            "Typisk noen måneder, mens vannet filtreres gjennom de øvre jordlagene",
            "Hundrevis til tusenvis av år, mens vannet siver gjennom geologiske lag",
            "Omtrent ett år, som er standardtiden for de fleste europeiske kilder"
        ],
        correct: 2,
        explanation: "Naturlig mineralvann har brukt hundrevis – og i noen tilfeller tusenvis – av år på å dannes. Vannet siver sakte ned gjennom jordlag og bergarter, hvor det gradvis tar opp mineraler fra geologiske formasjoner."
    },
    {
        question: "Hva er paradokset med Norge, melk og benskjørhet?",
        options: [
            "Nordmenn drikker generelt for lite melk sammenlignet med andre skandinaver",
            "Norge har høyt melkeforbruk men også høy forekomst av osteoporose",
            "Melk produsert i Norge inneholder faktisk for mye kalsium per liter",
            "Det finnes ikke noe dokumentert paradoks mellom disse faktorene"
        ],
        correct: 1,
        explanation: "Norge har et av verdens høyeste forbruk av meieriprodukter, men ligger også høyt på statistikken for osteoporose. En teori peker på at vi får lite kalsium og magnesium fra drikkevannet vårt – mineraler som er essensielle for benhelse."
    },
    {
        question: "Hva skiller ekte naturlig mineralvann juridisk fra andre typer vann?",
        options: [
            "Det må alltid inneholde kullsyre for å kunne klassifiseres som mineralvann",
            "Det må komme fra en beskyttet underjordisk kilde med dokumentert, stabil mineralsammensetning",
            "Det må være importert fra et annet land enn der det selges",
            "Det må utelukkende selges i glassflasker for å oppfylle kvalitetskravene"
        ],
        correct: 1,
        explanation: "Ekte naturlig mineralvann har en juridisk beskyttet definisjon i EU: det må komme fra en anerkjent underjordisk kilde med dokumentert og stabil mineralsammensetning, og tappes direkte ved kilden uten behandling som endrer vannets karakter."
    },
    {
        question: "Hvordan kan vannets mineralsammensetning påvirke smaken av kaffe og te?",
        options: [
            "Mineralinnholdet har ingen dokumentert effekt på smaken av varme drikker",
            "Mineralene påvirker ekstraksjon – for høyt kalsium gir bitter smak, for lavt gir flat smak",
            "Det er kun vannets temperatur som har reell betydning for smaksopplevelsen",
            "Bare pH-verdien i vannet spiller en rolle for hvordan kaffe og te smaker"
        ],
        correct: 1,
        explanation: "Vannets mineralsammensetning påvirker ekstraksjon og smak. Vann med moderat mineralinnhold og nøytral pH gir ofte best resultat. For høyt kalsiuminnhold kan gi bitter smak, mens for lavt mineralinnhold gir flat smak."
    },
    {
        question: "Hva er en 'blå sone' og hva har det med mineralvann å gjøre?",
        options: [
            "Geografiske områder med spesielt rent og uforurenset grunnvann",
            "Områder hvor mennesker lever lengst, ofte med tradisjon for mineralrikt vann",
            "Steder hvor det produseres og tappes naturlig mineralvann kommersielt",
            "Havområder med spesielt rent vann som brukes til flaske-produksjon"
        ],
        correct: 1,
        explanation: "Blå soner er områder hvor mennesker lever lengst og holder seg friske lengst. I mange av disse områdene har mineralvann en naturlig plass i hverdagen – ikke som en trend, men som en del av måltidet, kulturen og livsstilen."
    }
];

// English Questions
const questionsEN = [
    {
        question: "What is the main difference between natural mineral water and regular tap water?",
        options: [
            "Natural mineral water always has added carbonation to give it a fresh taste",
            "Natural mineral water contains minerals from geological sources and is untreated",
            "There is no real difference between them, only different marketing and packaging",
            "Tap water generally has higher mineral content because it is more thoroughly purified"
        ],
        correct: 1,
        explanation: "Natural mineral water comes from protected underground sources and contains minerals the body needs – such as magnesium, calcium, and bicarbonate – in a form that is easy for the body to absorb. Tap water is often filtered and purified, removing both contaminants and natural minerals."
    },
    {
        question: "Which mineral is often called the 'relaxation mineral' and is important for sleep and stress reduction?",
        options: [
            "Calcium, which supports the nervous system and improves sleep quality over time",
            "Sodium, which regulates the body's fluid balance and affects nerve function",
            "Magnesium, which supports the nervous system and promotes deep, restorative sleep",
            "Potassium, which balances electrolytes and contributes to muscle relaxation"
        ],
        correct: 2,
        explanation: "Magnesium is often called the 'relaxation mineral' because it supports the nervous system, promotes deep sleep, and reduces stress and anxiety. Over 300 enzymatic processes in the body depend on magnesium."
    },
    {
        question: "What does TDS (Total Dissolved Solids) mean when it comes to mineral water?",
        options: [
            "The water's temperature measured at the actual source where it is bottled",
            "The total amount of dissolved minerals and trace elements in the water",
            "The water's pH value indicating whether it is acidic or alkaline",
            "How many years the water has spent on its journey through the bedrock"
        ],
        correct: 1,
        explanation: "TDS measures the total amount of dissolved minerals in the water, expressed in mg/L. Low TDS (under 500) gives a light, neutral taste. Medium TDS (500-1500) gives more character. High TDS (over 1500) gives a strong mineral taste."
    },
    {
        question: "Why are minerals in natural mineral water easier for the body to absorb than supplements?",
        options: [
            "Because the mineral water has a lower temperature that makes absorption more efficient",
            "Because the minerals are already in ionic form and can be absorbed directly",
            "Because mineral water tastes better and thereby stimulates the digestive system",
            "There is actually no documented difference in how the body absorbs minerals"
        ],
        correct: 1,
        explanation: "Minerals in natural mineral water are already in ionic form, meaning the body can absorb them directly without having to break them down first. Supplements often need to go through digestion before they can be absorbed."
    },
    {
        question: "What function does bicarbonate have in mineral water?",
        options: [
            "It gives the water a characteristic color that varies from source to source",
            "It acts as a natural buffer against acid and supports digestion",
            "It lowers the water's temperature and keeps it cool for longer periods",
            "It increases the water's energy content and provides a natural invigorating effect"
        ],
        correct: 1,
        explanation: "Bicarbonate acts as a natural buffer against lactic acid during exercise and supports the body's acid-base balance. It can also relieve heartburn and acid reflux, and supports the liver's detoxification processes."
    },
    {
        question: "Why does Norway have mineral-poor drinking water?",
        options: [
            "Due to industrial pollution that has affected the groundwater over time",
            "Because the bedrock consists of hard rocks like granite that release few minerals",
            "Because it rains so much that minerals are constantly washed out of the soil",
            "Due to intensive water treatment that removes most natural minerals"
        ],
        correct: 1,
        explanation: "Norway's bedrock consists mainly of hard, crystalline rocks such as granite and gneiss, which release very few minerals into the groundwater. Countries with sedimentary rocks or limestone formations have natural conditions for mineral-rich water."
    },
    {
        question: "What are electrolytes and why are they important?",
        options: [
            "Natural bacterial cultures that help purify and balance the water",
            "Minerals with electrical charge that regulate fluid balance and muscle function",
            "Special vitamins that give the body energy and increase metabolism",
            "Proteins that the body uses to build and repair muscle tissue"
        ],
        correct: 1,
        explanation: "Electrolytes are minerals with electrical charge that regulate fluid balance, nerve signals, and muscle contractions. The most important are sodium, potassium, magnesium, and calcium. Imbalance can lead to cramps and fatigue."
    },
    {
        question: "In fine dining, how can mineral water affect the food experience?",
        options: [
            "It has no measurable effect on how we experience the taste of food",
            "It cools down the food and provides a more pleasant eating temperature",
            "The mineral content affects mouthfeel and can complement or enhance flavors",
            "It primarily functions as a replacement for salt in food preparation"
        ],
        correct: 2,
        explanation: "Mineral water has distinct flavor profiles that can complement food and wine. Water with high minerality can enhance the taste of rich dishes, while lighter water suits delicate flavors. It's about the total dining experience."
    },
    {
        question: "Which of these countries is NOT known for premium mineral water with high mineral content?",
        options: [
            "Slovenia, which has several world-famous sources with exceptional mineral content",
            "Norway, which has some of Europe's purest but also most mineral-poor water sources",
            "Germany, which is home to many of the world's most recognized mineral waters",
            "Italy, which has a long tradition of natural mineral water from alpine sources"
        ],
        correct: 1,
        explanation: "Norway has mineral-poor drinking water due to its bedrock. Slovenia (ROI), Germany (Gerolsteiner), and Italy (Marzia) all have geological conditions that provide naturally mineral-rich water."
    },
    {
        question: "Why do premium mineral water producers often use glass bottles?",
        options: [
            "Mainly to give the product a more exclusive and premium appearance",
            "Glass can be recycled infinitely, releases no substances, and protects water quality better",
            "Because plastic bottles are actually more expensive to produce in large quantities",
            "It is a legal requirement in the EU for all water sold as premium"
        ],
        correct: 1,
        explanation: "Glass can be recycled infinitely without loss of quality, releases no microplastics or chemicals into the water, and protects water quality better than plastic, which can be affected by light and temperature."
    },
    {
        question: "How long can it take for rainwater to become natural mineral water?",
        options: [
            "Usually just a few days, depending on the soil conditions in the area",
            "Typically a few months, while the water filters through the upper soil layers",
            "Hundreds to thousands of years, while the water seeps through geological layers",
            "About one year, which is the standard time for most European sources"
        ],
        correct: 2,
        explanation: "Natural mineral water has taken hundreds – and in some cases thousands – of years to form. The water slowly seeps down through soil layers and rock formations, where it gradually absorbs minerals from geological formations."
    },
    {
        question: "What is the paradox with Norway, milk and osteoporosis?",
        options: [
            "Norwegians generally drink too little milk compared to other Scandinavians",
            "Norway has high milk consumption but also high incidence of osteoporosis",
            "Milk produced in Norway actually contains too much calcium per liter",
            "There is no documented paradox between these factors"
        ],
        correct: 1,
        explanation: "Norway has one of the world's highest consumption of dairy products, but also ranks high in osteoporosis statistics. One theory points to the fact that we get little calcium and magnesium from our drinking water – minerals that are essential for bone health."
    },
    {
        question: "What legally distinguishes real natural mineral water from other types of water?",
        options: [
            "It must always contain carbonation to be classified as mineral water",
            "It must come from a protected underground source with documented, stable mineral composition",
            "It must be imported from a country other than where it is sold",
            "It must exclusively be sold in glass bottles to meet quality requirements"
        ],
        correct: 1,
        explanation: "Real natural mineral water has a legally protected definition in the EU: it must come from a recognized underground source with documented and stable mineral composition, and be bottled directly at the source without treatment that changes the water's character."
    },
    {
        question: "How can water's mineral composition affect the taste of coffee and tea?",
        options: [
            "The mineral content has no documented effect on the taste of hot beverages",
            "Minerals affect extraction – too high calcium gives bitter taste, too low gives flat taste",
            "Only the water temperature has any real significance for the taste experience",
            "Only the pH value of the water plays a role in how coffee and tea taste"
        ],
        correct: 1,
        explanation: "Water's mineral composition affects extraction and taste. Water with moderate mineral content and neutral pH often gives the best results. Too high calcium content can give a bitter taste, while too low mineral content gives a flat taste."
    },
    {
        question: "What is a 'Blue Zone' and what does it have to do with mineral water?",
        options: [
            "Geographic areas with particularly clean and unpolluted groundwater",
            "Areas where people live longest, often with a tradition of mineral-rich water",
            "Places where natural mineral water is commercially produced and bottled",
            "Ocean areas with especially clean water used for bottle production"
        ],
        correct: 1,
        explanation: "Blue Zones are areas where people live longest and stay healthy longest. In many of these areas, mineral water has a natural place in everyday life – not as a trend, but as part of meals, culture, and lifestyle."
    }
];

// Translations
const translations = {
    no: {
        pageTitle: 'Test din mineralkunnskap',
        pageDescription: 'Ta vår quiz og test hvor mye du kan om mineralvann og mineraler.',
        quizSubtitle: 'Interaktiv quiz',
        quizTitle: 'Test din mineralkunnskap',
        quizDescription: '15 spørsmål som tester din kunnskap om mineralvann, mineraler og helse.',
        question: 'Spørsmål',
        of: 'av',
        correct: 'riktige',
        confirmAnswer: 'Bekreft svar',
        nextQuestion: 'Neste spørsmål',
        seeResults: 'Se resultater',
        explanation: 'Forklaring',
        yourResult: 'Ditt resultat',
        youGot: 'Du fikk',
        correctAnswers: 'riktige svar',
        shareResult: 'Del resultatet ditt',
        tryAgain: 'Prøv igjen',
        learnMore: 'Lær mer i Mineral Guide',
        expert: 'Mineralekspert',
        enthusiast: 'Mineralentusiast',
        apprentice: 'Minerallærling',
        beginner: 'Mineralnybegynner'
    },
    en: {
        pageTitle: 'Test Your Mineral Knowledge',
        pageDescription: 'Take our quiz and test how much you know about mineral water and minerals.',
        quizSubtitle: 'Interactive quiz',
        quizTitle: 'Test Your Mineral Knowledge',
        quizDescription: '15 questions that test your knowledge about mineral water, minerals and health.',
        question: 'Question',
        of: 'of',
        correct: 'correct',
        confirmAnswer: 'Confirm answer',
        nextQuestion: 'Next question',
        seeResults: 'See results',
        explanation: 'Explanation',
        yourResult: 'Your result',
        youGot: 'You got',
        correctAnswers: 'correct answers',
        shareResult: 'Share your result',
        tryAgain: 'Try again',
        learnMore: 'Learn more in Mineral Guide',
        expert: 'Mineral Expert',
        enthusiast: 'Mineral Enthusiast',
        apprentice: 'Mineral Apprentice',
        beginner: 'Mineral Beginner'
    }
};

export default MineralQuizPage;
